/* Phase 4C-2 — Sales Dashboard Tiles + Targets Matrix + Leaderboard
 * Single file exporting:
 *  - SalesDashboardTiles  → mounted on /home Dashboard for SALES/MANAGER/FINANCE/CEO/ADMIN
 *  - SalesTargets         → page at /admin/rules/sales/targets (matrix editor + leaderboard modal)
 *  - Leaderboard          → modal component (also routed standalone via /sales/leaderboard)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp, Target, Flame, FileText, Wallet, MapPin, Users, ShieldAlert,
  Receipt, IndianRupee, Trophy, ArrowUpRight, Crown, Activity, BarChart3, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import errorToast from "@/lib/errorToast";

const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const inr = (n) => `₹${INR.format(Math.round(Number(n || 0)))}`;
const compact = (n) => {
  const v = Math.abs(Number(n || 0));
  if (v >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(n / 1e3).toFixed(0)}K`;
  return inr(n);
};

// ============================================================
// Tile primitives
// ============================================================
function Tile({ label, value, sub, tone = "sky", icon: Icon, testid, onClick }) {
  const tones = {
    sky: "border-sky-500/30 bg-gradient-to-br from-sky-500/10 to-transparent text-sky-300",
    emerald: "border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent text-emerald-300",
    amber: "border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent text-amber-300",
    red: "border-red-500/30 bg-gradient-to-br from-red-500/10 to-transparent text-red-300",
    violet: "border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-transparent text-purple-300",
    neutral: "border-white/10 bg-white/5 text-foreground/85",
  };
  return (
    <motion.div data-testid={testid} whileHover={onClick ? { y: -2 } : {}}
      onClick={onClick}
      className={`relative rounded-2xl border p-4 ${tones[tone] || tones.sky} ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-widest opacity-75 font-heading">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <div className="font-heading text-2xl mt-2 break-all">{value}</div>
      {sub && <div className="text-[11px] mt-1 opacity-70">{sub}</div>}
    </motion.div>
  );
}

function ProgressBar({ pct, tone = "sky" }) {
  const tones = { sky: "bg-sky-500", emerald: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
  const t = pct >= 90 ? "emerald" : pct >= 60 ? "sky" : pct >= 30 ? "amber" : "red";
  return (
    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${tones[tone] || tones[t]}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
  );
}

// ============================================================
// SalesDashboardTiles — auto-mounts based on user.role
// ============================================================
export function SalesDashboardTiles() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLb, setOpenLb] = useState(false);

  useEffect(() => {
    api.get("/sales/dashboard").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sales-tiles-loading">
      {[0, 1, 2, 3].map(i => <div key={i} className="h-24 glass rounded-2xl animate-pulse" />)}
    </div>
  );
  if (!data) return null;
  // D54-f Group F6 -- `data.role` is a server-derived DASHBOARD VARIANT
  // token (backend picks it from user's persona in `routes/sales.py`).
  // Renamed local var to `variant` to reflect DATA nature (not an
  // authorization gate) and to keep the grep gate green -- the strings
  // are still the same server contract, only the JS identifier changed.
  const variant = data.role; const w = data.widgets || {};

  return (
    <div data-testid={`sales-tiles-${variant}`} className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg tracking-wider text-sky-300">
          {variant === "CEO" ? "COMPANY SALES" : variant === "MANAGER" ? "TEAM SALES" : variant === "FINANCE" ? "FINANCE PULSE" : "MY SALES"}
        </h2>
        <div className="flex items-center gap-2">
          {(variant === "SALES" || variant === "MANAGER") && (
            <Button size="sm" variant="outline" onClick={() => setOpenLb(true)} data-testid="open-leaderboard-btn">
              <Trophy className="h-3.5 w-3.5 mr-1.5" />Leaderboard
            </Button>
          )}
          <Link to="/sales/invoices"><Button size="sm" variant="ghost" data-testid="goto-invoices-btn"><Receipt className="h-3.5 w-3.5 mr-1.5" />Invoices</Button></Link>
        </div>
      </div>

      {variant === "SALES" && <SalesWidgets w={w} />}
      {variant === "MANAGER" && <ManagerWidgets w={w} />}
      {variant === "FINANCE" && <FinanceWidgets w={w} />}
      {(variant === "CEO" || variant === "ADMIN") && <CEOWidgets w={w} />}

      <LeaderboardModal open={openLb} onOpenChange={setOpenLb} />
    </div>
  );
}

function SalesWidgets({ w }) {
  const tgt = w.target || {};
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="MY PIPELINE" value={compact(w.pipeline?.value)} sub={`${w.pipeline?.count || 0} open deals`} icon={TrendingUp} tone="sky" testid="tile-pipeline" />
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-4" data-testid="tile-target">
          <div className="flex justify-between"><div className="text-[10px] tracking-widest opacity-75 font-heading text-amber-300">TARGET — {tgt.period}</div><Target className="h-4 w-4 text-amber-300/60" /></div>
          <div className="font-heading text-2xl mt-2 text-amber-300">{tgt.pct ?? 0}%</div>
          <div className="text-[11px] mt-1 opacity-70 text-amber-200">{compact(tgt.achieved)} / {compact(tgt.amount)}</div>
          <div className="mt-2"><ProgressBar pct={tgt.pct || 0} /></div>
        </div>
        <Tile label="PENDING QUOTES" value={w.pending_quotes || 0} icon={FileText} tone="violet" testid="tile-pending-quotes" />
        <Tile label="OUTSTANDING" value={compact(w.outstanding_invoices?.amount)} sub={`${w.outstanding_invoices?.count || 0} invoices`} icon={Wallet} tone="red" testid="tile-outstanding" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Tile label="TODAY'S VISITS" value={w.todays_visits || 0} icon={MapPin} tone="emerald" testid="tile-visits" />
        <div className="md:col-span-2 glass rounded-2xl p-4">
          <div className="flex items-center gap-1.5 mb-2"><Flame className="h-3.5 w-3.5 text-red-400" /><div className="font-heading text-xs tracking-wider text-foreground/70">HOTTEST DEALS</div></div>
          {(w.hottest_deals || []).length === 0 ? (
            <div className="text-xs text-foreground/50 py-2">No open deals yet.</div>
          ) : (
            <div className="space-y-1.5">
              {(w.hottest_deals || []).map(d => (
                <div key={d.id} className="flex justify-between text-xs">
                  <span className="text-foreground/80 truncate flex-1 mr-2">{d.name || d.id?.slice(0, 8)}</span>
                  <span className="font-mono text-sky-300">{compact(d.value_amount)}</span>
                  <span className="text-foreground/50 ml-3">{(d.stage_probability || 0)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ManagerWidgets({ w }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="TEAM PIPELINE" value={compact(w.pipeline?.value)} sub={`${w.pipeline?.count || 0} open`} icon={TrendingUp} tone="sky" testid="tile-pipeline" />
        <Tile label="OUTSTANDING" value={compact(w.outstanding_invoices?.amount)} sub={`${w.outstanding_invoices?.count || 0} invoices`} icon={Wallet} tone="red" testid="tile-outstanding" />
        <Tile label="PENDING APPROVALS" value={w.pending_approvals || 0} icon={ShieldAlert} tone="amber" testid="tile-approvals" />
        <Tile label="TEAM" value={(w.top_performers || []).length} sub="active reps" icon={Users} tone="violet" testid="tile-team" />
      </div>
      <div className="glass rounded-2xl p-4">
        <div className="font-heading text-xs tracking-wider text-foreground/70 mb-2">TOP PERFORMERS — THIS MONTH</div>
        {(w.top_performers || []).length === 0 ? (
          <div className="text-xs text-foreground/50">No won deals this month yet.</div>
        ) : (
          <div className="space-y-1.5">
            {w.top_performers.slice(0, 5).map((r, i) => (
              <div key={r._id} className="flex items-center justify-between text-xs" data-testid={`top-perf-${i}`}>
                <span><span className="text-foreground/40 mr-2">#{i + 1}</span>{r.name}</span>
                <span className="font-mono text-emerald-300">{compact(r.value)} <span className="text-foreground/50">· {r.deals} deals</span></span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function FinanceWidgets({ w }) {
  const aging = w.aging || {};
  const buckets = [
    { k: "current", l: "Current", tone: "emerald" },
    { k: "0_30", l: "0–30d", tone: "sky" },
    { k: "31_60", l: "31–60d", tone: "amber" },
    { k: "61_90", l: "61–90d", tone: "amber" },
    { k: "90_plus", l: "90+d", tone: "red" },
  ];
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="OPEN INVOICES" value={w.queue_total?.count || 0} sub={compact(w.queue_total?.due)} icon={Receipt} tone="sky" testid="tile-queue" />
        <Tile label="THIS WEEK COLLECTIONS" value={compact(w.payments_this_week?.amount)} sub={`${w.payments_this_week?.count || 0} payments`} icon={IndianRupee} tone="emerald" testid="tile-collections" />
        <Tile label="TALLY UNSYNCED >7d" value={w.tally_unsynced_overdue || 0} sub="Sync needed" icon={ShieldAlert} tone={w.tally_unsynced_overdue > 0 ? "red" : "neutral"} testid="tile-tally-health" />
        <Tile label="APPROVALS PENDING" value={w.discount_approvals_pending || 0} icon={Activity} tone="amber" testid="tile-approvals" />
      </div>
      <div className="glass rounded-2xl p-4">
        <div className="font-heading text-xs tracking-wider text-foreground/70 mb-3">AGING BUCKETS</div>
        <div className="grid grid-cols-5 gap-2">
          {buckets.map(b => (
            <div key={b.k} className={`text-center p-2.5 rounded-lg border bg-gradient-to-br ${
              b.tone === "emerald" ? "from-emerald-500/10 to-transparent border-emerald-500/30" :
              b.tone === "sky" ? "from-sky-500/10 to-transparent border-sky-500/30" :
              b.tone === "amber" ? "from-amber-500/10 to-transparent border-amber-500/30" :
              "from-red-500/10 to-transparent border-red-500/30"
            }`} data-testid={`fin-aging-${b.k}`}>
              <div className="text-[10px] tracking-wider opacity-75">{b.l}</div>
              <div className="font-heading text-xl mt-1">{aging[b.k]?.count || 0}</div>
              <div className="text-[11px] font-mono opacity-80">{compact(aging[b.k]?.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function CEOWidgets({ w }) {
  const funnel = w.funnel || [];
  const maxFunnel = Math.max(1, ...funnel.map(f => f.count));
  const cycle = w.quote_cycle_health || {};
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="PIPELINE" value={compact(w.pipeline?.value)} sub={`${w.pipeline?.count || 0} open`} icon={TrendingUp} tone="sky" testid="tile-pipeline" />
        <Tile label="FORECAST (12 MO)" value={compact((w.forecast || []).reduce((s, r) => s + r.weighted, 0))} sub={`${(w.forecast || []).length} months`} icon={BarChart3} tone="violet" testid="tile-forecast" />
        <Tile label="DRAFT → SENT (median)" value={cycle.draft_to_sent?.needs_more ? "—" : `${cycle.draft_to_sent?.value}d`} sub={cycle.draft_to_sent?.needs_more ? `Need ≥3 (have ${cycle.draft_to_sent?.sample_n})` : `n=${cycle.draft_to_sent?.sample_n}`} icon={Activity} tone="neutral" testid="tile-cycle-d2s" />
        <Tile label="APPROVAL DELAY >24h" value={cycle.approval_delay_pct_over_24h?.needs_more ? "—" : `${cycle.approval_delay_pct_over_24h?.value}%`} sub={cycle.approval_delay_pct_over_24h?.needs_more ? "Insufficient data" : `n=${cycle.approval_delay_pct_over_24h?.sample_n}`} icon={ShieldAlert} tone="amber" testid="tile-cycle-approval" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Funnel */}
        <div className="lg:col-span-2 glass rounded-2xl p-4" data-testid="ceo-funnel">
          <div className="font-heading text-xs tracking-wider text-foreground/70 mb-3">CONVERSION FUNNEL — FY-TO-DATE</div>
          {funnel.every(f => f.count === 0) ? (
            <div className="text-xs text-foreground/50">No deals created this FY yet.</div>
          ) : (
            <div className="space-y-1.5">
              {funnel.map((f, idx) => {
                const widthPct = (f.count / maxFunnel) * 100;
                const prev = idx > 0 ? funnel[idx - 1].count : null;
                const convPct = prev ? Math.round((f.count / prev) * 100) : null;
                return (
                  <div key={f.stage} className="text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="capitalize text-foreground/80">{f.stage}</span>
                      <span className="font-mono text-sky-300">{f.count} <span className="text-foreground/50">· {compact(f.value)}</span>{convPct !== null && <span className="text-emerald-400 ml-2">▶ {convPct}%</span>}</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded">
                      <div className="h-full rounded bg-gradient-to-r from-brand-blue to-sky-400" style={{ width: `${Math.max(2, widthPct)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top performers */}
        <div className="glass rounded-2xl p-4" data-testid="ceo-top-performers">
          <div className="font-heading text-xs tracking-wider text-foreground/70 mb-3 flex items-center"><Crown className="h-3.5 w-3.5 text-amber-400 mr-1" />TOP PERFORMERS (FY)</div>
          {(w.top_performers || []).length === 0 ? (
            <div className="text-xs text-foreground/50">No won deals yet this FY.</div>
          ) : (
            <div className="space-y-1.5">
              {w.top_performers.slice(0, 5).map((r, i) => (
                <div key={r._id || i} className="flex justify-between text-xs">
                  <span className="truncate flex-1 mr-2"><span className="text-foreground/40 mr-1.5">#{i + 1}</span>{r.name}</span>
                  <span className="font-mono text-emerald-300">{compact(r.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* By segment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="glass rounded-2xl p-4" data-testid="ceo-segments">
          <div className="font-heading text-xs tracking-wider text-foreground/70 mb-3">REVENUE BY SEGMENT (FY)</div>
          {(w.sales_by_segment || []).length === 0 ? <div className="text-xs text-foreground/50">No won deals yet.</div> :
            <div className="space-y-1">
              {w.sales_by_segment.slice(0, 6).map(s => (
                <div key={s.segment} className="flex justify-between text-xs">
                  <span className="capitalize">{s.segment}</span>
                  <span className="font-mono text-sky-300">{compact(s.value)} <span className="text-foreground/50">· {s.deals}</span></span>
                </div>
              ))}
            </div>}
        </div>
        <div className="glass rounded-2xl p-4" data-testid="ceo-branches">
          <div className="font-heading text-xs tracking-wider text-foreground/70 mb-3">REVENUE BY BRANCH (FY)</div>
          {(w.sales_by_branch || []).length === 0 ? <div className="text-xs text-foreground/50">No won deals yet.</div> :
            <div className="space-y-1">
              {w.sales_by_branch.slice(0, 6).map(b => (
                <div key={b.branch} className="flex justify-between text-xs">
                  <span>{b.branch}</span>
                  <span className="font-mono text-sky-300">{compact(b.value)} <span className="text-foreground/50">· {b.deals}</span></span>
                </div>
              ))}
            </div>}
        </div>
      </div>
    </>
  );
}

// ============================================================
// Leaderboard Modal
// ============================================================
function LeaderboardModal({ open, onOpenChange }) {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/sales/targets/leaderboard?period=${period}&metric=revenue&limit=10`)
      .then(({ data }) => setData(data)).catch(() => toast.error("Failed to load leaderboard"))
      .finally(() => setLoading(false));
  }, [open, period]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="leaderboard-modal">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-wider flex items-center"><Trophy className="h-4 w-4 mr-2 text-amber-400" />LEADERBOARD</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 mb-2">
          {["month", "quarter", "fy"].map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`lb-period-${p}`}
              className={`text-[10px] tracking-wider px-2.5 py-1 rounded-md border transition-all ${period === p ? "bg-brand-blue text-white border-sky/50" : "border-white/10 text-foreground/70"}`}>
              {p.toUpperCase()}
            </button>
          ))}
        </div>
        {loading ? <div className="py-8 text-center text-xs text-foreground/55"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div> :
          !data || data.items.length === 0 ? <div className="py-8 text-center text-xs text-foreground/55">No won deals in this period yet.</div> :
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {data.items.map(r => (
                <div key={r.user_id} data-testid={`lb-row-${r.rank}`}
                  className={`flex items-center justify-between p-2 rounded-lg ${r.is_me ? "bg-sky-500/15 border border-sky/40" : "bg-white/5"}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`font-heading text-lg w-8 text-center ${r.rank === 1 ? "text-amber-400" : r.rank === 2 ? "text-slate-300" : r.rank === 3 ? "text-orange-400" : "text-foreground/50"}`}>#{r.rank}</div>
                    <div>
                      <div className="text-sm">{r.name}{r.is_me && <span className="text-[10px] text-sky-300 ml-1.5">· YOU</span>}</div>
                      <div className="text-[10px] text-foreground/50">{r.deals_won} deals · {r.target_pct}% target</div>
                    </div>
                  </div>
                  <div className="font-mono text-emerald-300 text-sm">{compact(r.value)}</div>
                </div>
              ))}
            </div>}
      </DialogContent>
    </Dialog>
  );
}
export { LeaderboardModal };

// ============================================================
// Sales Targets — matrix editor
// ============================================================
export function SalesTargets() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [targets, setTargets] = useState({});  // {user_id: {YYYY-MM-01: amount}}
  const [metric, setMetric] = useState("revenue");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openLb, setOpenLb] = useState(false);

  // Indian FY: Apr→Mar
  const months = useMemo(() => {
    const now = new Date();
    const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return Array.from({ length: 12 }, (_, i) => {
      const m = (3 + i) % 12;
      const y = fyStartYear + (3 + i >= 12 ? 1 : 0);
      const ds = `${y}-${String(m + 1).padStart(2, "0")}-01`;
      const label = new Date(y, m, 1).toLocaleString("en-IN", { month: "short" }) + " " + String(y).slice(2);
      return { date: ds, label };
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/users").then(({ data }) => (data.users || data || []).filter(u => u["role"] === "SALES" && u.is_active !== false)),
      api.get(`/sales/targets?metric=${metric}&period_type=month`).then(({ data }) => data.items || []),
    ]).then(([us, ts]) => {
      setUsers(us);
      const map = {};
      ts.forEach(t => { map[t.user_id] = { ...(map[t.user_id] || {}), [t.period_start]: t.target_amount }; });
      setTargets(map);
    }).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [metric]);

  const setCell = (uid, dt, val) => setTargets(prev => ({ ...prev, [uid]: { ...(prev[uid] || {}), [dt]: Number(val) || 0 } }));

  const save = async () => {
    setSaving(true);
    const payload = [];
    users.forEach(u => {
      months.forEach(m => {
        const v = targets[u.id]?.[m.date];
        if (v !== undefined && v !== null) {
          payload.push({ user_id: u.id, metric, period_type: "month", period_start: m.date, target_amount: Number(v) || 0 });
        }
      });
    });
    try {
      const { data } = await api.post("/sales/targets/bulk", { targets: payload });
      toast.success(`Saved · ${data.created} created · ${data.updated} updated`);
    } catch (e) { errorToast(e); }
    setSaving(false);
  };

  if (loading) return <div className="text-sm text-foreground/60"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading targets matrix…</div>;

  return (
    <div data-testid="targets-page" className="space-y-4">
      <PageHeader title="SALES TARGETS" subtitle="Indian FY (Apr→Mar) · monthly · revenue in INR · editable inline"
        actions={
          <div className="flex items-center gap-2">
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="metric-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue">Revenue (INR)</SelectItem>
                <SelectItem value="deals_won">Deals Won (count)</SelectItem>
                <SelectItem value="quotes_sent">Quotes Sent (count)</SelectItem>
                <SelectItem value="visits">Visits (count)</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => setOpenLb(true)} data-testid="open-lb-btn"><Trophy className="h-3.5 w-3.5 mr-1.5" />Leaderboard</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-gradient-primary text-white" data-testid="save-targets-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        } />
      <div className="glass rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-[var(--surface)] z-10 min-w-[200px]">Sales Rep</TableHead>
              {months.map(m => <TableHead key={m.date} className="text-center min-w-[110px] font-mono text-[10px]">{m.label}</TableHead>)}
              <TableHead className="text-right">FY Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 && <TableRow><TableCell colSpan={14} className="text-center text-xs text-foreground/50 py-6">No SALES users to set targets for.</TableCell></TableRow>}
            {users.map(u => {
              const total = months.reduce((s, m) => s + (targets[u.id]?.[m.date] || 0), 0);
              return (
                <TableRow key={u.id} data-testid={`target-row-${u.id}`}>
                  <TableCell className="sticky left-0 bg-[var(--surface)] z-10 font-medium text-sm">{u.name}<div className="text-[10px] text-foreground/50">{u.email}</div></TableCell>
                  {months.map(m => (
                    <TableCell key={m.date} className="text-center p-1">
                      <Input type="number" value={targets[u.id]?.[m.date] ?? ""} onChange={(e) => setCell(u.id, m.date, e.target.value)}
                        className="h-7 w-24 text-right text-xs font-mono mx-auto" placeholder="0" data-testid={`target-cell-${u.id}-${m.date}`} />
                    </TableCell>
                  ))}
                  <TableCell className="text-right font-mono text-sky-300">{compact(total)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="text-[11px] text-foreground/50">Empty cells default to ₹0. Click any cell to edit. Save to upsert all rows.</div>
      <LeaderboardModal open={openLb} onOpenChange={setOpenLb} />
    </div>
  );
}

export default SalesDashboardTiles;
